<template>
  <div class="field-mapping">
    <LoadingSpinner v-if="isLoading" />

    <b-modal ref="modal" centered laze no-close-on-esc hide-header-close no-close-on-backdrop :hide-header="true"
      :hide-footer="true">
      <div class="text-center">
        Uploading file, please wait while we upload the file to our server. You will be redirected once the file was
        successfully uploaded.
        <img src="/images/loading.gif" alt="loading.io">
      </div>
    </b-modal>

    <div class="card">
      <div class="card-header blue">Field Mapping</div>
      <div class="card-body">
        <b-row>
          <b-col cols="8">
            <table class="table mapping-table">
              <thead>
                <tr>
                  <th>GrandShipper Fields</th>
                  <th width="100"></th>
                  <th>Your File's Column Headers</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(col, i) in columns" :key="i">
                  <td class="text-right">{{ col.name }}</td>
                  <td>
                    <div class="column-info">
                      <a v-if="col.rules" v-b-popover.hover.html="popoverMethod(col.rules)" variant="info">
                        <b-img src="/images/icons/information.png" />
                      </a>
                    </div>
                  </td>
                  <td>
                    <b-select :options="fieldOptions(col.name)" v-model="field_map[i]" @change="onFieldSelect(i)"
                      size="sm" :class="['d-block', 'w-100', { 'is-invalid': isRequired(col.name) && !field_map[i] }]">
                    </b-select>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-col>
          <b-col>
            <label-preview class="position-affix" :origin="origin" :label="label_preview"
              style="max-width: 350px"></label-preview>
          </b-col>
        </b-row>

        <b-row class="justify-content-between px-3">
          <b-btn class="btn-blue" size="sm" @click="$emit('update:view', 'upload');">
            <span class="glyphicon glyphicon-arrow-left"></span> Preview Page
          </b-btn>
          <b-btn class="btn-blue" :disabled="isMappingInvalid || uploading" size="sm" @click="onSubmit">
            <span v-if="uploading">
              Please Wait, uploading file ....
            </span>
            <span v-else>
              Process Import
              <span class="glyphicon glyphicon-arrow-right"></span>
            </span>
          </b-btn>
        </b-row>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.field-mapping {
  .card {
    margin-bottom: 1.5rem;

    .card-header {
      color: #fff;
      font-size: 0.95rem !important;
      padding: 0.10rem 1rem;
    }
  }

  .table {

    th,
    td {
      font-size: .95rem;
      padding: 0.5rem;
      position: relative;
    }
  }
}

.column-info {
  margin-top: -9px;
  margin-left: -13px;

  a {
    color: #334caa !important;
    cursor: pointer;
  }

  img {
    width: 15px;
  }
}
</style>

<script>
import LoadingSpinner from "../../../components/LoadingSpinner";

import { mapGetters } from "vuex";

export default {
  components: {
    LoadingSpinner,
  },
  props: ['view', 'origin', 'file', 'headers', 'row'],
  data() {
    return {
      uploading: false,
      isLoading: false,
      deselected: [],
      field_map: [],
      selected: [],
      label: {},
      mapped_headers: {},
      mapped_fields: {},
      cols: {
        order_number: {
          name: "Order #",
          rules: ["25 maximum numeric characters only."]
        },
        ref_id: {
          name: "Reference ID",
          rules: ["25 maximum alphanumeric, exceeding characters will be truncated."]
        },
        first_name: {
          name: "First Name",
          rules: ["This field is required. (At least one field is required)"]
        },
        last_name: {
          name: "Last Name",
          rules: ["This field is required. (At least one field is required)"]
        },
        company: {
          name: "Company",
          rules: ["This field is required. (At least one field is required)"]
        },
        address: {
          name: "Address 1",
          rules: [
            "This field is required.",
            "Requires a numerical number"
          ]
        },
        address2: {
          name: "Address 2"
        },
        city: {
          name: "City",
          rules: ["This field is required."]
        },
        state: {
          name: "State/Province",
          rules: [
            "This field is required.",
            "State must be 2 letter formats."
          ]
        },
        postal_code: {
          name: "Postal Code",
          rules: ["This field is required."]
        },
        country: {
          name: "Country"
        },
        service: {
          name: "Service Class",
          rules: ["This field is required."]
        },
        package_type: {
          name: "Package Type",
          rules: ["This field is required."]
        },
        weight_lb: {
          name: "Weight (lb)",
          rules: ["This field is required. (At least one field is required)"]
        },
        weight_oz: {
          name: "Weight (oz)",
          rules: ["This field is required. (At least one field is required)"]
        },
        length: {
          name: "Length (in)"
        },
        width: {
          name: "Width (in)"
        },
        height: {
          name: "Height (in)"
        },
        declared_value: {
          name: "Declared Value"
        },
        email: {
          name: "Email"
        },
        phone: {
          name: "Phone #"
        },
        postmark_date: {
          name: "Postmark Date"
        },
        signature: {
          name: "Signature"
        },
        insurance: {
          name: "Insurance"
        },
        label_sticky: {
          name: "Custom Field 1",
          rules: ["40 maximum alphanumeric, exceeding characters will be truncated."]
        },
        label_sticky_2: {
          name: "Custom Field 2",
          rules: ["40 maximum alphanumeric, exceeding characters will be truncated."]
        },
        description: {
          name: "Description",
          rules: ["100 maximum alphanumeric, exceeding characters will be truncated."]
        },
        quantity: {
          name: "Quantity",
        },
        package_content_type: {
          name: "Package Content Type",
        },
        extra_1: {
          name: "Note 1",
          rules: ["25 maximum alphanumeric, exceeding characters will be truncated."]
        },
        extra_2: {
          name: "Note 2",
          rules: ["25 maximum alphanumeric, exceeding characters will be truncated."]
        },
        extra_3: {
          name: "Note 3",
          rules: ["25 maximum alphanumeric, exceeding characters will be truncated."]
        },
        extra_4: {
          name: "Note 4",
          rules: ["25 maximum alphanumeric, exceeding characters will be truncated."]
        },
        extra_5: {
          name: "Note 5",
          rules: ["25 maximum alphanumeric, exceeding characters will be truncated."]
        },
        // ship from
        shipfrom_first_name: {
          name: "ShipFrom First Name",
        },
        shipfrom_last_name: {
          name: "ShipFrom Last Name",
        },
        shipfrom_company: {
          name: "ShipFrom Company",
        },
        shipfrom_address: {
          name: "ShipFrom Address 1",
        },
        shipfrom_address2: {
          name: "ShipFrom Address 2"
        },
        shipfrom_city: {
          name: "ShipFrom City",
        },
        shipfrom_state: {
          name: "ShipFrom State",
        },
        shipfrom_postal_code: {
          name: "ShipFrom Postal Code",
        },
      }
    }
  },
  computed: {
    ...mapGetters({
      user: "getUser",
    }),

    columns() {
      let headers = this.headers,
        field_map = [],
        valid = false;
      let options = _.map(this.cols, function (v) {
        headers.forEach(f => {
          //! note: f = csv raw header & v = system recognized headers
          const vv = v.name.toLowerCase().trim();
          const ff = f.toLowerCase().trim();
          if (vv === "order #" && (ff === "order #" || ff === "order number")) {
            field_map.push(f);
            valid = true;
          } else if (vv === "reference id" && (ff === "ref id" || ff === "reference id")) {
            field_map.push(f);
            valid = true;
          } else if (vv === "state/province" && (ff === "state" || ff === "province" || ff === "state/province")) {
            field_map.push(f);
            valid = true;
          } else if (vv === "shipfrom state" && (ff === "shipfrom state" || ff === "shipfrom province")) {
            field_map.push(f);
            valid = true;
          } else if (vv === "package type" && ff === "package") {
            field_map.push(f);
            valid = true;
          } else if (vv === "service class" && (ff === "service" || ff === "service type" || ff === "service class")) {
            field_map.push(f);
            valid = true;
          } else if (vv === "length (in)" && ff === "length") {
            field_map.push(f);
            valid = true;
          } else if (vv === "width (in)" && ff === "width") {
            field_map.push(f);
            valid = true;
          } else if (vv === "height (in)" && ff === "height") {
            field_map.push(f);
            valid = true;
          } else if (vv === "weight (lb)" && ff === "weight (lbs)") {
            field_map.push(f);
            valid = true;
          } else if (vv === "phone #" && ff === "phone number") {
            field_map.push(f);
            valid = true;
          } else if (vv === "insurance" && (ff === "insurance" || ff === "shipsurance")) {
            field_map.push(f);
            valid = true;
          } else if (ff === vv) {
            field_map.push(f);
            valid = true;
          }
        });
        if (!valid) field_map.push(null);
        valid = false;
        return v;
      });
      this.field_map = field_map;
      return options
    },
    field_options() {
      let options = this.headers
        .filter(f => f !== "")
        .map(f => {
          return {
            value: f,
            text: f,
          }
        });
      return [
        {
          value: null,
          text: "-- Ignore --"
        },
        ...options
      ];
    },
    label_preview() {
      let lb = {},
        headers = {},
        mapped_fields = {};

      for (const [i, [k, v]] of Object.entries(Object.entries(this.cols))) {
        let field = this.field_map[i];

        headers[v.name] = field;  // used in validation in isRequired function
        mapped_fields[k] = field; // passed mapped fields to the backend

        if (field == "Weight (lbs)") {
          field = "Weight (lb)"
        } else if (field == "Service") {
          field = "Service Class"
        } else if (field == "Package)") {
          field = "Package Type"
        }
        // else if (field == "State") {
        //   field = "State/Province"
        // }

        lb[k] = field;  // used in label preview
      }

      this.mapped_headers = headers;
      this.mapped_fields = mapped_fields;

      return {
        name: this.label[lb.buyer] || null,
        first_name: this.label[lb.first_name] || null,
        last_name: this.label[lb.last_name] || null,
        company: this.label[lb.company] || null,
        address: this.label[lb.address] || null,
        address2: this.label[lb.address2] || null,
        city: this.label[lb.city] || null,
        state: this.label[lb.state] || null,
        postal_code: this.label[lb.postal_code] || null,
        country: this.label[lb.country] || "US",
        label_sticky: this.label[lb.label_sticky] || null,
        label_sticky_2: this.label[lb.label_sticky_2] || null,
      }
    },

    isMappingInvalid() {
      const req_fields = ["address", "city", "state", "postal_code", "service", "package_type"]
      const req_if_1 = ["company", "first_name", "last_name"]
      const req_if_2 = ["weight_lb", "weight_oz"]
      let isInvalid = false
      let cnt_1 = 0;
      let cnt_2 = 0;

      req_if_1.forEach(f => {
        if (_.isEmpty(this.mapped_fields[f])) cnt_1++;
        isInvalid = cnt_1 >= req_if_1.length;
      })
      if (!isInvalid) {
        req_if_2.forEach(f => {
          if (_.isEmpty(this.mapped_fields[f])) cnt_2++;
          isInvalid = cnt_2 >= req_if_2.length;
        })
      }
      if (!isInvalid) {
        req_fields.forEach(f => {
          if (_.isEmpty(this.mapped_fields[f])) isInvalid = true
        })
      }
      return isInvalid
    },
  },
  mounted() {
    // mapping the row for label preview with grandshipper fields (this.cols)
    let row = this.row,
      new_label = [];

    _.forEach(this.cols, function (f, k) {
      _.forEach(row, function (t, v) {
        const vv = v.toLowerCase().trim();
        const ff = f.toString().toLowerCase().trim();
        if (ff === "state/province" && (vv === "state" || vv === "province" || vv === "state/province")) {
          new_label[f] = t;
        } else if (ff === "package type" && vv === "package") {
          new_label[f] = t;
        } else if (ff === "service class" && (vv === "service" || vv === "service class" || vv === "service type")) {
          new_label[f] = t;
        } else if (ff === "length (in)" && vv === "length") {
          new_label[f] = t;
        } else if (ff === "width (in)" && vv === "width") {
          new_label[f] = t;
        } else if (ff === "height (in)" && vv === "height") {
          new_label[f] = t;
        } else if (ff === "weight (lb)" && vv === "weight (lbs)") {
          new_label[f] = t;
        } else if (ff === "phone #" && vv === "phone number") {
          new_label[f] = t;
        } else if (ff === vv) {
          new_label[f] = t;
        } else {
          new_label[v] = t;
        }
      })
    })
    this.label = new_label;
  },
  methods: {
    popoverMethod(rules) {
      let html = '';
      rules.forEach(rule => html += `<li> ${rule} </li>`)
      return `<ul class="m-0 pl-3"> ${html} </ul>`
    },
    fieldOptions(col = null) {
      let options = this.field_options;
      if (col == 'Country') {
        options = Object.assign({}, options, [{ value: null, text: "-- U.S. is default for domestic --" }]);
      }
      return options;
    },
    isRequired(col) {
      const req_fields = ["Address 1", "City", "State/Province", "Postal Code", "Service Class", "Package Type"]
      const req_if_1 = ["Company", "First Name", "Last Name"]
      const req_if_2 = ["Weight (lb)", "Weight (oz)"]
      let required = false;

      if (req_if_1.includes(col)) {
        let cnt_1 = 0;
        req_if_1.forEach(f => {
          if (_.isEmpty(this.mapped_headers[f])) cnt_1++;
        })
        if (cnt_1 >= req_if_1.length) required = true;
      }
      if (req_if_2.includes(col)) {
        let cnt_2 = 0;
        req_if_2.forEach(f => {
          if (_.isEmpty(this.mapped_headers[f])) cnt_2++;
        })
        if (cnt_2 >= req_if_2.length) required = true;
      }
      if (req_fields.includes(col)) required = true;
      return required;
    },

    onFieldSelect(i) {
      return function (val) {
        this.field_map = _.map(this.field_map, (field, index) => {
          if (field === val && index != i) {
            field = null;
          }
          return field;
        })
      };
    },
    async onSubmit(e) {
      e.preventDefault();
      this.uploading = true;
      this.$refs.modal.show();

      try {
        let formData = new FormData();
        formData.append('order_status_id', 2); // save as order_type: import
        formData.append('user_id', this.user.id);
        formData.append('origin', JSON.stringify(this.origin));
        formData.append('file', this.file);
        formData.append('field_map', JSON.stringify(this.mapped_fields));

        await axios.post('/orders/order-import', formData)
        this.$router.push('/orders/import');
      } catch (error) {
        // alert("Problem uploading file, it may contain invalid character.\r\nPlease double check the file and try again. \r\n \r\nIf this keeps happening please contact us.");

        const { response: { data: { errors, message } } } = error;
        console.error("Err message: ", message)
        console.error("Err Fields: ", errors)
        let errorMessage = message;
        if (errors) {
          errorMessage = errors.reduce((all, current) => {
            return `${all} &nbsp;&nbsp;- ${current} <br />`
          }, "Plase double check CSV File: <br />")
        }
        console.error(error.response.data.errors);
        this.$parent.$emit("alert", {
          message: errorMessage.trim(),
          type: "danger",

        });

        // this.$router.push('/orders/import');
      } finally {
        this.$refs.modal.hide();
        this.uploading = false;
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// resources/assets/js/views/Orders/Import/FieldMapping.vue